/**
 * HS 분류 Hub — 5 카드 grid 진입점.
 * 사이드바 'HS 분류' 단일 메뉴 → 클릭 시 이 페이지 → 카드 클릭으로 sub-page.
 *
 * 4 카드:
 *  1. 🤖 AI 분류 (HS Code 분류) — line item → HSK + 법령·선례 (Dual-Evidence)
 *  2. 🔎 통합 검색 — HS 통합검색 (new)
 *  3. 📚 분류사례 탐색 (12K) — pgroonga FTS + pgvector hybrid + 즐겨찾기
 *  4. 📖 동의어 사전 관리 — power user (관세사) FTS 동의어 CRUD
 *
 * 분류 이력 카드는 페이지 미구현으로 보류.
 */
import React from "react";
import {
  Sparkles,
  Search,
  Database,
  BookOpen,
  Building2,
  Layers,
  Info,
  ArrowRight,
} from "lucide-react";
import { useLang } from "./i18n";
import AuditSideNav from "./AuditSideNav";
import "./theme.css";

const CARDS = [
  {
    id: "overview",
    icon: Info,
    titleKey: "hub.card.overview.title",
    descKey: "hub.card.overview.desc",
    href: "/?audit=onto-overview",
    accent: "#00897b",
  },
  {
    id: "ai",
    icon: Sparkles,
    titleKey: "hub.card.ai.title",
    descKey: "hub.card.ai.desc",
    href: "/?audit=precedents&open=ai", // 분류사례 탐색 + AI panel 자동 open
    accent: "#1a73e8",
  },
  {
    id: "unified",
    icon: Search,
    titleKey: "hub.card.unified.title",
    descKey: "hub.card.unified.desc",
    href: "/?audit=main",
    accent: "#673ab7",
  },
  {
    id: "precedents",
    icon: Database,
    titleKey: "hub.card.precedents.title",
    descKey: "hub.card.precedents.desc",
    href: "/?audit=precedents",
    accent: "#0f9d58",
  },
  {
    id: "synonyms",
    icon: BookOpen,
    titleKey: "hub.card.synonyms.title",
    descKey: "hub.card.synonyms.desc",
    href: "/?audit=synonyms",
    accent: "#f9a825",
  },
  {
    id: "trade-partners",
    icon: Building2,
    titleKey: "hub.card.tradePartners.title",
    descKey: "hub.card.tradePartners.desc",
    href: "/?audit=trade-partners",
    accent: "#d93025",
  },
  {
    id: "onto-search",
    icon: Layers,
    titleKey: "hub.card.ontoSearch.title",
    descKey: "hub.card.ontoSearch.desc",
    href: "/?audit=onto-search",
    accent: "#00897b",
  },
];

export default function HsHubPage() {
  const { t, lang, setLang } = useLang();

  const goCard = (href) => {
    window.location.href = href;
  };

  return (
    <div
      className="audit-root"
      style={{
        minHeight: "100vh",
        background: "var(--audit-bg)",
        color: "var(--audit-text)",
        display: "flex",
      }}
    >
      <AuditSideNav />
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 상단 바 */}
        <div
          style={{
            padding: "12px 24px",
            borderBottom: "1px solid var(--audit-border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h1 style={{ fontSize: 18, margin: 0, fontWeight: 700 }}>
            {t("hub.title")}
          </h1>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              marginLeft: 4,
            }}
          >
            {t("hub.subtitle")}
          </span>
          <div style={{ flex: 1 }} />
          <button
            className="audit-btn"
            onClick={() => setLang(lang === "ko" ? "en" : "ko")}
            title={`Switch language (current: ${lang.toUpperCase()})`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontFamily: "JetBrains Mono, monospace",
              fontWeight: 600,
            }}
          >
            {lang.toUpperCase()}
          </button>
        </div>

        {/* 카드 grid */}
        <div
          style={{
            padding: 32,
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => goCard(c.href)}
                style={{
                  background: "var(--audit-bg-elevated)",
                  border: "1px solid var(--audit-border)",
                  borderRadius: 10,
                  padding: 20,
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  fontFamily: "inherit",
                  color: "var(--audit-text)",
                  transition: "all 0.15s",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = c.accent;
                  e.currentTarget.style.boxShadow = `0 4px 12px ${c.accent}22`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--audit-border)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: c.accent + "22",
                      color: c.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <h2
                    style={{
                      fontSize: 15,
                      margin: 0,
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    {t(c.titleKey)}
                  </h2>
                  <ArrowRight
                    size={14}
                    style={{ color: "var(--audit-text-muted)" }}
                  />
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    lineHeight: 1.5,
                  }}
                >
                  {t(c.descKey)}
                </p>
              </button>
            );
          })}
        </div>

        {/* footer 안내 */}
        <div
          style={{
            padding: "16px 24px",
            fontSize: 12,
            color: "var(--audit-text-faint)",
            textAlign: "center",
          }}
        >
          {t("hub.footer")}
        </div>
      </div>
    </div>
  );
}
